import { Link, useNavigate } from "react-router-dom";
import { Show, UserButton, useAuth, useUser } from "@clerk/react";
import { Heart, Shield, Trophy, Upload, Play, Flame, Users, GraduationCap, Crown } from "lucide-react";
import { useEffect, useState } from "react";
import { makeApi } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { isAndroidApp } from "@/lib/platform";

export default function Nav({ lives: livesProp }) {
  const { getToken, isSignedIn } = useAuth();
  const { user } = useUser();
  const [me, setMe] = useState(null);
  const navigate = useNavigate();
  const { icon_url } = useBranding();

  useEffect(() => {
    if (!isSignedIn) return;
    const api = makeApi(getToken);
    api.get("/me").then((r) => setMe(r.data)).catch((e) => console.error("Failed to load profile", e));
  }, [isSignedIn, getToken]);

  const lives = livesProp ?? me?.lives ?? 0;

  return (
    <nav className="sticky top-0 z-30 bg-white border-b-[3px] border-[#374151]">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          {icon_url ? (
            <img src={icon_url} alt="TP" data-testid="nav-logo-img"
              className="w-10 h-10 rounded-full border-[3px] border-[#374151] object-cover bg-white shadow-[0_3px_0_0_#374151]" />
          ) : (
            <img src="/icons/icon-192.png" alt="TP" data-testid="nav-logo-default"
              className="w-10 h-10 rounded-full border-[3px] border-[#374151] object-cover bg-white shadow-[0_3px_0_0_#374151]" />
          )}
          <span className="font-black text-xl tracking-tight">TP Quiz</span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <Show when="signed-in">
            <Link to="/play" data-testid="nav-play" className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full border-[2px] border-[#374151] bg-white font-bold text-sm hover:bg-slate-50">
              <Play size={16} strokeWidth={2.5} /> Play
            </Link>
            <Link to="/leaderboard" data-testid="nav-leaderboard" className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full border-[2px] border-[#374151] bg-white font-bold text-sm hover:bg-slate-50">
              <Trophy size={16} strokeWidth={2.5} /> Leaders
            </Link>
            <Link to="/submit" data-testid="nav-submit" className="hidden md:flex items-center gap-1 px-3 py-1.5 rounded-full border-[2px] border-[#374151] bg-white font-bold text-sm hover:bg-slate-50">
              <Upload size={16} strokeWidth={2.5} /> Submit
            </Link>
            <Link to="/classes" data-testid="nav-classes" className="hidden lg:flex items-center gap-1 px-3 py-1.5 rounded-full border-[2px] border-[#374151] bg-white font-bold text-sm hover:bg-slate-50">
              <Users size={16} strokeWidth={2.5} /> Classes
            </Link>
            {(me?.role === "teacher" || me?.role === "admin") && (
              <Link to="/teach" data-testid="nav-teach" className="hidden lg:flex items-center gap-1 px-3 py-1.5 rounded-full border-[2px] border-[#374151] bg-[#E6F7FF] font-bold text-sm hover:brightness-95">
                <GraduationCap size={16} strokeWidth={2.5} /> Teach
              </Link>
            )}
            {me?.role === "admin" && (
              <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-1 px-3 py-1.5 rounded-full border-[2px] border-[#374151] bg-[var(--tp-accent)] font-bold text-sm hover:brightness-95">
                <Shield size={16} strokeWidth={2.5} /> Admin
              </Link>
            )}
            {me && !me.subscribed && me.role !== "admin" && !isAndroidApp() && (
              <Link to="/pricing" data-testid="nav-go-pro" className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full border-[2px] border-[#374151] bg-[var(--tp-success)] text-white font-black text-sm hover:brightness-95">
                <Crown size={16} strokeWidth={2.5} /> Go Pro
              </Link>
            )}
            <Link to="/profile" data-testid="streak-counter" title="Daily streak"
              className="flex items-center gap-1 px-3 py-1.5 rounded-full border-[2px] border-[#374151] bg-[#FFF0E6] text-[#F97316] font-black text-sm">
              <Flame size={16} fill="#F97316" strokeWidth={2.5} /> {me?.streak ?? 0}
            </Link>
            <div data-testid="lives-counter" className="flex items-center gap-1 px-3 py-1.5 rounded-full border-[2px] border-[#374151] bg-[#FFE1E1] text-[#FF4B4B] font-black text-sm">
              <Heart size={16} fill="#FF4B4B" strokeWidth={2.5} /> {me?.subscribed ? "∞" : lives}
            </div>
            <UserButton afterSignOutUrl="/" />
          </Show>
          <Show when="signed-out">
            <button data-testid="nav-signin" onClick={() => navigate("/sign-in")}
              className="px-4 py-2 rounded-full border-[3px] border-[#374151] bg-white font-black text-sm shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none">
              Sign in
            </button>
            <button data-testid="nav-signup" onClick={() => navigate("/sign-up")}
              className="px-4 py-2 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black text-sm shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none">
              Get started
            </button>
          </Show>
        </div>
      </div>
    </nav>
  );
}
